import { useCallback, useMemo, useState } from 'react'

import { Icon, Link, T, VStack } from 'govwell-ui'
import FormControlLabel from 'govwell-ui/components/FormControl/FormControlLabel'
import SegmentedControl from 'govwell-ui/components/SegmentedControl/SegmentedControl'
import { SelectModeProps } from 'govwell-ui/components/Select/types'
import {
  AddressEntryType,
  EntityLayerFragment,
  type SearchEntityVersionFragment,
} from 'types/graphql'

import EntityVersionFieldsTooltipContent from 'src/components/EntityVersionFieldsTooltipContent'
import { useApplyConditionsSelection } from 'src/components/RecordTemplateApplyForm/ApplyConditionsContext'
import { useGISAutoFill } from 'src/components/RecordTemplateApplyForm/GISAutoFillContext'
import EntityVersionSearchMap from 'src/components/Selects/EntityVersionSearch/EntityVersionSearchMap'
import EntityVersionSearchMapModal from 'src/components/Selects/EntityVersionSearch/EntityVersionSearchMapModal'
import EntityVersionSearchSelect from 'src/components/Selects/EntityVersionSearch/EntityVersionSearchSelect'
import {
  EntityVersionGISSelectionData,
  SelectValue,
} from 'src/components/Selects/EntityVersionSearch/types'
import { useGetEntityVersionByIds } from 'src/fetch/entityVersions'
import useDisclosure from 'src/hooks/use-disclosure'
import { MapModeType } from 'src/types'
import { FeatureGate, useFeatureFlagging } from 'src/utils/featureFlagging/featureFlagging'
import { getSearchEntityLayersCaption } from 'src/utils/gis'
import { monitoring, MonitoringContextKey, MonitoringEvent } from 'src/utils/monitoring'

const DEFAULT_APPLY_CONDITIONS_FIELD_KEY = '__entity-version-search__'

type Props = SelectModeProps<SelectValue> & {
  addressEntryType: AddressEntryType
  caption?: React.ReactNode
  disabledEntityIds?: Set<number>
  entityLayers: EntityLayerFragment[]
  errorMessage?: string
  isClearable?: boolean
  isInvalid?: boolean
  isLoadingEntityLayers?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  mapModeType?: MapModeType
  onBlur?: () => void
  onGisSelection?: (data: EntityVersionGISSelectionData) => void
  placeholder?: string
  tooltip?: string | null
  width?: React.CSSProperties['width']
  applyConditionsFieldKey?: string
}

const EntityVersionSearchInternals = (props: Props) => {
  const {
    mapModeType = 'none',
    label: labelProp,
    width,
    caption,
    tooltip,
    onValueChange,
    isMultiSelect,
    value,
    onGisSelection: onGisSelectionProp,
    applyConditionsFieldKey = DEFAULT_APPLY_CONDITIONS_FIELD_KEY,
    ...commonSelectProps
  } = props

  const { isRequired, entityLayers, onBlur } = commonSelectProps
  const { onGISSelection: onGISSelectionContext } = useGISAutoFill()
  const { onEntityVersionsChange } = useApplyConditionsSelection()

  const { checkFeatureFlag } = useFeatureFlagging()
  const isEntityVersionMapModeEnabled = checkFeatureFlag(FeatureGate.EntityVersionMapMode)

  const isSegmentedControlEnabled = mapModeType === 'inline' && isEntityVersionMapModeEnabled

  const [segmentedKey, setSegmentedKey] = useState<'select' | 'map'>('select')

  const { isOpen: isMapModalOpen, close: onMapModalClose, open: openMapModal } = useDisclosure()

  const handleMapModalClose = useCallback(() => {
    onMapModalClose()
    onBlur?.()
  }, [onMapModalClose, onBlur])

  const isMapMode = segmentedKey === 'map' || isMapModalOpen

  const handleLaunchMapTelemetry = useCallback(() => {
    monitoring.captureEvent(MonitoringEvent.EntityVersionSearchMapLaunch, {
      [MonitoringContextKey.IsMultiSelect]: isMultiSelect,
      [MonitoringContextKey.EntityVersionSearchMapModeType]: mapModeType,
    })
  }, [isMultiSelect, mapModeType])

  const handleSegmentedKeyChange = useCallback(
    (updatedValue: 'select' | 'map') => {
      if (updatedValue === 'map') {
        handleLaunchMapTelemetry()
      }
      setSegmentedKey(updatedValue)
    },
    [handleLaunchMapTelemetry]
  )

  const handleOpenMapModal = useCallback(() => {
    handleLaunchMapTelemetry()
    openMapModal()
  }, [handleLaunchMapTelemetry, openMapModal])

  const handleValueChangeTelemetry = useCallback(() => {
    monitoring.captureEvent(MonitoringEvent.EntityVersionSearchItemSelected, {
      [MonitoringContextKey.IsMultiSelect]: isMultiSelect,
      [MonitoringContextKey.IsEntityVersionMapMode]: isMapMode,
      [MonitoringContextKey.EntityVersionSearchMapModeType]: mapModeType,
    })
  }, [isMultiSelect, isMapMode, mapModeType])

  const handleValueChangeSingleSelect = useCallback(
    (updatedValue: SelectValue | null | undefined) => {
      if (isMultiSelect) {
        return
      }

      handleValueChangeTelemetry()
      onValueChange(updatedValue)

      // Notify parent that a GIS selection was made (only for non-manual entries)
      if (updatedValue && typeof updatedValue !== 'string') {
        onGisSelectionProp?.({
          entityVersionId: updatedValue.id,
          entityVersion: updatedValue,
        })

        // Trigger GIS auto-fill via context (if available)
        void onGISSelectionContext?.({ entityVersionId: updatedValue.id })

        onEntityVersionsChange([updatedValue], applyConditionsFieldKey)
      } else {
        onEntityVersionsChange([], applyConditionsFieldKey)
      }
    },
    [
      isMultiSelect,
      onValueChange,
      handleValueChangeTelemetry,
      onGisSelectionProp,
      onGISSelectionContext,
      onEntityVersionsChange,
      applyConditionsFieldKey,
    ]
  )

  const handleValueChangeMultiSelect = useCallback(
    (updatedValue: SelectValue[]) => {
      if (!isMultiSelect) {
        return
      }

      handleValueChangeTelemetry()
      onValueChange(updatedValue)

      const entityVersions = updatedValue.filter(
        (item): item is SearchEntityVersionFragment => typeof item !== 'string'
      )
      onEntityVersionsChange(entityVersions, applyConditionsFieldKey)
    },
    [
      isMultiSelect,
      onValueChange,
      handleValueChangeTelemetry,
      onEntityVersionsChange,
      applyConditionsFieldKey,
    ]
  )

  // recreate the SelectModeProps object so that we can add telemetry side effects to the onValueChange function
  const selectModeProps = useMemo(() => {
    return isMultiSelect
      ? {
          isMultiSelect: true as const,
          value,
          onValueChange: handleValueChangeMultiSelect,
        }
      : {
          isMultiSelect: false as const,
          value,
          onValueChange: handleValueChangeSingleSelect,
        }
  }, [isMultiSelect, value, handleValueChangeMultiSelect, handleValueChangeSingleSelect])

  const selectedOptionSingleSelect = useMemo(() => {
    if (isMultiSelect) {
      return undefined
    }
    return props.value
  }, [isMultiSelect, props.value])

  // use this to show the list of fields for the selected entity version, if it's a single select
  const { entityVersions, isLoading: isLoadingEntityVersionFields } = useGetEntityVersionByIds(
    typeof selectedOptionSingleSelect !== 'string' && selectedOptionSingleSelect
      ? [selectedOptionSingleSelect.id]
      : null
  )

  const fields = useMemo(() => {
    return entityVersions?.[0]?.fieldsForEntityVersionAndIntersectingEntityVersions ?? []
  }, [entityVersions])

  const label = useMemo(() => {
    if (isLoadingEntityVersionFields) {
      return (
        <T.Body>
          {labelProp}
          &nbsp;
          <Icon.FaSpinner className="animate-spin" />
        </T.Body>
      )
    }
    if (!fields.length) {
      return labelProp
    }

    return <EntityVersionFieldsTooltipContent fields={fields} label={labelProp} />
  }, [fields, isLoadingEntityVersionFields, labelProp])

  const defaultCaption = useMemo(() => {
    const captionStr = getSearchEntityLayersCaption(entityLayers)

    return mapModeType === 'modal' ? (
      <Link onClick={handleOpenMapModal}>{captionStr} on a map</Link>
    ) : (
      captionStr
    )
  }, [entityLayers, mapModeType, handleOpenMapModal])

  return (
    <VStack width={width}>
      <FormControlLabel
        id={undefined}
        isLabelRedundant={false}
        label={label}
        isRequired={!!isRequired}
        tooltip={tooltip}
        className="mb-[3px]" // match the gap of the Antd FormItem label
      />
      <VStack gap={4} align="center">
        {isSegmentedControlEnabled && (
          <SegmentedControl
            ariaLabel="Address or map mode"
            onValueChange={handleSegmentedKeyChange}
            value={segmentedKey}
          >
            <SegmentedControl.Item icon="FaLocationDot" value="select">
              Address
            </SegmentedControl.Item>
            <SegmentedControl.Item icon="FaMap" value="map">
              Map
            </SegmentedControl.Item>
          </SegmentedControl>
        )}
        {segmentedKey === 'select' ? (
          <EntityVersionSearchSelect
            {...commonSelectProps}
            width="100%"
            caption={caption ?? defaultCaption}
            {...selectModeProps}
          />
        ) : (
          <EntityVersionSearchMap
            {...commonSelectProps}
            entityLayers={entityLayers}
            {...selectModeProps}
          />
        )}
      </VStack>
      {isMapModalOpen && (
        <EntityVersionSearchMapModal
          {...commonSelectProps}
          entityLayers={entityLayers}
          onClose={handleMapModalClose}
          {...selectModeProps}
        />
      )}
    </VStack>
  )
}

export default React.memo(EntityVersionSearchInternals)
